import { supabase } from '@/integrations/supabase/client';
import { AppState } from '@/types';
import { transformAppStateFromSupabase } from '@/utils/supabaseDataTransforms';
import { loadHabitsFromSupabase, loadDailiesFromSupabase, loadTodosFromSupabase } from '@/utils/supabaseHabitLoader';

export async function loadUserDataFromSupabase(userId: string): Promise<AppState | null> {
  try {
    // Load user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (profileError) {
      console.error('Error loading profile:', profileError);
    }

    // Load user stats
    const { data: stats, error: statsError } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (statsError) {
      console.error('Error loading stats:', statsError);
    }

    // Load activities (all of them for accurate point calculation)
    console.log('🔍 Loading activities for user:', userId);
    const { data: activities, error: activitiesError } = await supabase
      .from('activities')
      .select(`
        *,
        activity_comments (
          *
        )
      `)
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });

    // Load user profile separately to get the name
    const userProfile = profile || await supabase
      .from('profiles')
      .select('name, email')
      .eq('user_id', userId)
      .single()
      .then(({ data }) => data);

    console.log('📈 Raw database query returned:', activities?.length || 0, 'activities');
    console.log('🔍 First few activities:', activities?.slice(0, 3)?.map(a => ({ id: a.id, name: a.name, points: a.points, timestamp: a.timestamp })));
    console.log('🔍 Activities error:', activitiesError);
    
    if (activitiesError) {
      console.error('❌ Error loading activities:', activitiesError);
      console.error('❌ Error details:', {
        message: activitiesError.message,
        code: activitiesError.code,
        hint: activitiesError.hint
      });
    }

    // Load simple pet
    const { data: simplePet, error: simplePetError } = await supabase
      .from('simple_pets')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (simplePetError) {
      console.error('Error loading simple pet:', simplePetError);
    }

    // Load virtual pet
    const { data: pet, error: petError } = await supabase
      .from('virtual_pets')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (petError) {
      console.error('Error loading pet:', petError);
    }

    // Load challenges - with better error handling
    let challenges = [];
    try {
      const { data: challengeData, error: challengesError } = await supabase
        .from('challenges')
        .select('*')
        .or(`user_id.eq.${userId},is_public.eq.true`)
        .order('created_at', { ascending: false })
        .limit(20); // Add pagination limit

      if (challengesError) {
        console.error('Error loading challenges:', challengesError);
      } else {
        challenges = challengeData || [];
      }
    } catch (error) {
      console.error('Failed to load challenges:', error);
      // Continue with empty challenges array
    }

    // Load habits, dailies, and todos
    console.log('🔍 Loading habits, dailies, and todos for user:', userId);
    const [habits, dailies, todos] = await Promise.all([
      loadHabitsFromSupabase(userId),
      loadDailiesFromSupabase(userId), 
      loadTodosFromSupabase(userId)
    ]);

    const allHabits = [...habits, ...dailies, ...todos];
    console.log('📊 Total habits loaded:', allHabits.length, '(habits:', habits.length, 'dailies:', dailies.length, 'todos:', todos.length, ')');

    return transformAppStateFromSupabase(
      profile,
      stats,
      activities || [],
      pet,
      challenges,
      userProfile,
      allHabits,
      simplePet
    );

  } catch (error) {
    console.error('Error loading user data:', error);
    return null;
  }
}